import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import SimpleFilters from './SimpleFilters';

/**
 * Views that have something relevant to put in the filter rail.
 *
 * The rail is a filter panel and nothing else — navigation lives in NavRail on
 * desktop and MobileNav on a phone — so a view with no relevant controls gets
 * no rail at all. An empty panel headed "Filters" is worse than none.
 *
 * Admin Hub and Profile used to inherit the map's entity filters, layer toggles
 * and H3 sliders simply because renderContent's `default` case was the map
 * panel. Listing the views explicitly means a new one shows nothing until
 * someone decides what belongs there.
 */
export const VIEWS_WITH_FILTER_PANEL = ['map', 'list', 'visits', 'graph'];

export const hasFilterPanel = (view) => VIEWS_WITH_FILTER_PANEL.includes(view);

// SideBar is now a pure filter panel.
// Navigation (view switching, logo, platform switcher) lives in NavRail.
// User profile / logout lives in NavRail.
// This component only owns: entity filters, map layer toggles, H3 range sliders, reset.
//
// Props:
//   activeView — drives context-aware content; see VIEWS_WITH_FILTER_PANEL
//   collapsed  — self-managed via localStorage; desktop only (mobile always shows full panel)
const SideBar = ({
  // H3 layer controls
  onH3SalesRangeChange,
  h3SalesFilterRange,
  onH3TrafficRangeChange,
  h3TrafficFilterRange,
  maxSalesValue,
  maxTrafficValue,
  onH3ViewChange,
  h3ActiveView,
  onH3RevenueRangeChange,
  h3RevenueFilterRange,
  maxRevenueValue,
  isH3Visible,
  onResetH3Filters,
  // { traffic, population }: layers with real data (utils/areaLayers.js)
  areaLayers = { traffic: false, population: false },
  // Org-imported datasets ([{ id, name, metrics: [{ key, label }] }]) and the active one
  areaDatasets = [],
  activeDatasetLayer = null,
  onDatasetLayerChange,
  // Mobile overlay control — NavRail is desktop-only; mobile still uses this panel
  isMobileMenuOpen = false,
  onCloseMobileMenu,
  // Simple three-tier entity filters
  onSimpleFilterChange,
  currentSimpleFilters,
  // Context-aware content: which view is active
  activeView = 'map',
  // Visit filters (hosted here when activeView === 'visits')
  visitViewMode,
  onVisitViewModeChange,
  visitFilterMode,
  onVisitFilterChange,
  overdueCount = 0,
  // Called with the new collapsed boolean whenever the sidebar is toggled
  onCollapsedChange,
}) => {
  // Collapsed state is desktop-only. Persisted to localStorage so it survives refreshes.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar_collapsed') === 'true'; } catch { return false; }
  });

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('sidebar_collapsed', String(next)); } catch {}
      onCollapsedChange?.(next);
      return next;
    });
  };

  // Guard: wait for filter data before rendering filter controls
  if (!h3SalesFilterRange || !h3TrafficFilterRange) {
    return <div className="w-64 bg-gray-900 text-gray-50 p-4">Loading filters...</div>;
  }

  // --- COLLAPSED STRIP (desktop only) ---
  // On mobile, isMobileMenuOpen drives the full panel regardless of collapsed state.
  // The collapsed strip is hidden on mobile (hidden md:flex) so mobile is unaffected.
  if (collapsed && !isMobileMenuOpen) {
    return (
      <div className="hidden md:flex flex-col items-center bg-gray-900 w-10 h-screen flex-shrink-0 border-r border-gray-800 py-4 gap-3">
        <button aria-label="Expand filters"
          onClick={toggleCollapsed}
          title="Expand filters"
          className="p-2 text-gray-500 hover:text-gray-50 rounded-lg hover:bg-gray-800 transition-colors"
        >
          <ChevronRight size={16} />
        </button>
        <span
          className="text-gray-600 text-xs font-medium select-none"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
        >
          Filters
        </span>
      </div>
    );
  }

  // --- CONTEXT-AWARE CONTENT ---
  // Each view renders only the controls relevant to it.

  const renderContent = () => {
    switch (activeView) {
      case 'list':
        return (
          <>
            {/* Entity filters are just as useful for the list view */}
            <div className="px-4 pt-4 pb-1">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Entity Filters</div>
            </div>
            {onSimpleFilterChange && currentSimpleFilters && (
              <SimpleFilters
                onFilterChange={onSimpleFilterChange}
                currentFilters={currentSimpleFilters}
              />
            )}
            {/* Map Layers section intentionally hidden for list view */}
            <div className="p-4 border-t border-gray-700 mt-auto">
              <button
                onClick={onResetH3Filters}
                className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition-colors text-white text-sm"
              >
                Reset Filters
              </button>
            </div>
          </>
        );

      case 'visits':
        // Visit filters live in the sidebar so they don't compete with the
        // visit cards for screen real-estate. TodaysVisits renders in the
        // right split panel and receives these selections via props.
        //
        // Layout intent: Route is the primary working view, so its filters
        // are the first thing the user sees. History is a secondary log of
        // past visits and sits at the bottom as a single entry. Picking a
        // Route filter implicitly switches view mode back to 'route' so the
        // user doesn't have to juggle two controls.
        return (
          <div className="flex-1 flex flex-col">
            {/* Route section — header + filter chips */}
            <div className="p-4 border-b border-gray-800">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-base">🚀</span>
                <span className="text-xs font-semibold text-gray-300 uppercase tracking-wider">Route</span>
                {visitViewMode === 'route' && (
                  <span className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-400" aria-hidden="true" />
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                {/* Strict 3-filter contract per the Visit System spec —
                    no "All Planned" / "Needs Scheduling" parking lots. Every
                    visit in the active route falls into exactly one of these
                    time-bound buckets, and date-less visits don't exist
                    (ad-hoc defaults to today at creation time). */}
                {[
                  { key: 'today', label: '📍 Today', badge: null },
                  { key: 'scheduled', label: '📅 Scheduled', badge: null },
                  { key: 'overdue', label: '⚠️ Overdue', badge: overdueCount > 0 ? overdueCount : null },
                ].map(({ key, label, badge }) => {
                  const isActive = visitViewMode === 'route' && visitFilterMode === key;
                  return (
                    <button
                      key={key}
                      onClick={() => {
                        // handleVisitFilterChange in App.js handles the route-mode switch
                        // internally, so no concurrent onVisitViewModeChange call needed here.
                        if (onVisitFilterChange) onVisitFilterChange(key);
                      }}
                      data-testid={`sidebar-visit-filter-${key}`}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        isActive
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-gray-400 hover:text-gray-50 hover:bg-gray-800'
                      }`}
                    >
                      <span>{label}</span>
                      {badge !== null && (
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                          isActive ? 'bg-white/20 text-white' : 'bg-red-500/20 text-red-300'
                        }`}>
                          {badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Spacer pushes History + tip to the bottom of the rail */}
            <div className="flex-1" />

            {/* History — single log-style entry at the bottom */}
            <div className="px-4 pt-3 pb-2 border-t border-gray-800">
              <button
                onClick={() => onVisitViewModeChange && onVisitViewModeChange('history')}
                data-testid="sidebar-visit-view-history"
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  visitViewMode === 'history'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md shadow-purple-900/30'
                    : 'text-gray-400 hover:text-gray-50 hover:bg-gray-800'
                }`}
              >
                <span className="text-base">📜</span>
                <span className="flex-1 text-left">History</span>
                <span className={`text-[10px] font-normal ${
                  visitViewMode === 'history' ? 'text-blue-100' : 'text-gray-500'
                }`}>
                  Past visits
                </span>
              </button>
            </div>

            {/* Hint */}
            <div className="px-4 pb-4">
              <p className="text-xs text-gray-500 leading-relaxed">
                Tip: use the search inside the Visits panel to find a specific stop.
              </p>
            </div>
          </div>
        );

      case 'graph':
        return (
          <div className="p-4 flex-1 flex flex-col gap-3">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Graph Intelligence</div>
            <p className="text-xs text-gray-500 leading-relaxed">
              Ask natural language questions about your territory. The knowledge graph answers in real time.
            </p>
            <div className="mt-2 space-y-1.5">
              {[
                { label: 'Underserved markets', desc: 'Industries with low competition' },
                { label: 'User preferences', desc: 'Your AVOIDS/PREFERS edges' },
                { label: 'Saturation zones', desc: 'Areas >70% competition' },
                { label: 'Visit patterns', desc: 'Your 30-day history' },
              ].map(({ label, desc }) => (
                <div key={label} className="px-3 py-2 rounded-lg bg-gray-800/60 border border-gray-700/50">
                  <p className="text-xs font-medium text-gray-300">{label}</p>
                  <p className="text-[10px] text-gray-500">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'map':
        // Full filter panel: entity filters + map layers + H3 sliders + reset
        return (
          <>
            {/* Three-tier entity filters */}
            {onSimpleFilterChange && currentSimpleFilters && (
              <SimpleFilters
                onFilterChange={onSimpleFilterChange}
                currentFilters={currentSimpleFilters}
              />
            )}

            {/* Map Layers */}
            <div className="p-4 border-t border-gray-700 flex-1">
              <div className="text-xs font-semibold text-gray-400 mb-3">MAP LAYERS</div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-800 p-2 rounded transition-colors">
                  <input
                    type="checkbox"
                    checked={h3ActiveView === 'sales' && isH3Visible}
                    onChange={() => onH3ViewChange('sales')}
                    className="w-4 h-4 rounded border-gray-600 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-gray-900"
                  />
                  <span className="text-sm text-gray-50">Sales Data</span>
                </label>
                {areaLayers.traffic && (
                  <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-800 p-2 rounded transition-colors">
                    <input
                      type="checkbox"
                      checked={h3ActiveView === 'traffic' && isH3Visible}
                      onChange={() => onH3ViewChange('traffic')}
                      className="w-4 h-4 rounded border-gray-600 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-gray-900"
                    />
                    <span className="text-sm text-gray-50">Traffic Density</span>
                  </label>
                )}
                <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-800 p-2 rounded transition-colors">
                  <input
                    type="checkbox"
                    checked={h3ActiveView === 'revenue' && isH3Visible}
                    onChange={() => onH3ViewChange('revenue')}
                    className="w-4 h-4 rounded border-gray-600 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-gray-900"
                  />
                  <span className="text-sm text-gray-50">Revenue (Transactions)</span>
                </label>
                {areaLayers.population && (
                  <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-800 p-2 rounded transition-colors">
                    <input
                      type="checkbox"
                      checked={h3ActiveView === 'population' && isH3Visible}
                      onChange={() => onH3ViewChange('population')}
                      className="w-4 h-4 rounded border-gray-600 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-gray-900"
                    />
                    <span className="text-sm text-gray-50">Population Density</span>
                  </label>
                )}
                {areaDatasets.length > 0 && (
                  <div className="pt-2" data-testid="area-dataset-layers">
                    <div className="px-2 pb-1 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Your area data</div>
                    {areaDatasets.flatMap(ds => ds.metrics.map(m => (
                      <label key={`${ds.id}:${m.key}`} className="flex items-center gap-2 cursor-pointer hover:bg-gray-800 p-2 rounded transition-colors">
                        <input
                          type="checkbox"
                          checked={Boolean(activeDatasetLayer && activeDatasetLayer.datasetId === ds.id && activeDatasetLayer.key === m.key)}
                          onChange={() => onDatasetLayerChange?.(ds.id, m.key)}
                          className="w-4 h-4 rounded border-gray-600 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-gray-900"
                        />
                        <span className="text-sm text-gray-50 truncate" title={ds.name}>
                          {ds.metrics.length > 1 ? `${ds.name} · ${m.label}` : ds.name}
                        </span>
                      </label>
                    )))}
                  </div>
                )}
                <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-800 p-2 rounded transition-colors">
                  <input type="checkbox" disabled className="w-4 h-4 rounded border-gray-600" />
                  <span className="text-sm text-gray-500">Territory Zones</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-800 p-2 rounded transition-colors">
                  <input type="checkbox" disabled className="w-4 h-4 rounded border-gray-600" />
                  <span className="text-sm text-gray-500">Weather</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-800 p-2 rounded transition-colors">
                  <input type="checkbox" disabled className="w-4 h-4 rounded border-gray-600" />
                  <span className="text-sm text-gray-500">Competition</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-800 p-2 rounded transition-colors">
                  <input type="checkbox" disabled className="w-4 h-4 rounded border-gray-600" />
                  <span className="text-sm text-gray-500">Sales Heatmap</span>
                </label>
              </div>

              {/* H3 range sliders — shown when a layer is active */}
              {isH3Visible && (
                <div className="mt-4 space-y-4 p-3 bg-gray-800 rounded-lg">
                  {h3ActiveView === 'sales' && (
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-gray-400 block mb-3">
                        SALES RANGE: ${h3SalesFilterRange[0].toLocaleString()} - ${h3SalesFilterRange[1].toLocaleString()}
                      </label>
                      <Slider
                        range
                        min={0}
                        max={maxSalesValue}
                        value={h3SalesFilterRange}
                        onChange={onH3SalesRangeChange}
                        trackStyle={[{ backgroundColor: '#10b981' }]}
                        handleStyle={[
                          { borderColor: '#10b981', backgroundColor: '#10b981' },
                          { borderColor: '#10b981', backgroundColor: '#10b981' }
                        ]}
                        railStyle={{ backgroundColor: 'rgb(var(--c-gray-700))' }}
                      />
                    </div>
                  )}
                  {h3ActiveView === 'traffic' && (
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-gray-400 block mb-3">
                        TRAFFIC RANGE: {h3TrafficFilterRange[0]} - {h3TrafficFilterRange[1]}
                      </label>
                      <Slider
                        range
                        min={0}
                        max={maxTrafficValue}
                        value={h3TrafficFilterRange}
                        onChange={onH3TrafficRangeChange}
                        trackStyle={[{ backgroundColor: '#10b981' }]}
                        handleStyle={[
                          { borderColor: '#10b981', backgroundColor: '#10b981' },
                          { borderColor: '#10b981', backgroundColor: '#10b981' }
                        ]}
                        railStyle={{ backgroundColor: 'rgb(var(--c-gray-700))' }}
                      />
                    </div>
                  )}
                  {h3ActiveView === 'revenue' && h3RevenueFilterRange && (
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-gray-400 block mb-3">
                        REVENUE RANGE: ¥{h3RevenueFilterRange[0].toLocaleString()} - ¥{h3RevenueFilterRange[1].toLocaleString()}
                      </label>
                      <Slider
                        range
                        min={0}
                        max={maxRevenueValue}
                        value={h3RevenueFilterRange}
                        onChange={onH3RevenueRangeChange}
                        trackStyle={[{ backgroundColor: '#f59e0b' }]}
                        handleStyle={[
                          { borderColor: '#f59e0b', backgroundColor: '#f59e0b' },
                          { borderColor: '#f59e0b', backgroundColor: '#f59e0b' }
                        ]}
                        railStyle={{ backgroundColor: 'rgb(var(--c-gray-700))' }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Reset button */}
            <div className="p-4 border-t border-gray-700">
              <button
                onClick={onResetH3Filters}
                className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition-colors text-white text-sm"
              >
                Reset Filters
              </button>
            </div>
          </>
        );

      default:
        // No relevant controls for this view — see VIEWS_WITH_FILTER_PANEL.
        return null;
    }
  };

  // --- EXPANDED PANEL ---
  // Mobile: full-screen overlay driven by isMobileMenuOpen (unchanged behavior)
  // Desktop: fixed-width w-64 panel, always visible via md:relative md:translate-x-0
  return (
    <div
      className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 text-gray-50 flex flex-col flex-shrink-0
        transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        md:relative md:translate-x-0 md:h-screen
        overflow-y-auto scroll-pane
      `}
      style={{
        WebkitOverflowScrolling: 'touch',
        maxHeight: 'var(--app-height)'
      }}
    >
      {/* Filter panel header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Filters</span>
        <div className="flex items-center gap-1">
          {/* Mobile close button — desktop panel is always visible, mobile needs explicit close */}
          <button
            onClick={onCloseMobileMenu}
            className="md:hidden text-gray-400 hover:text-gray-50 transition-colors p-1"
            aria-label="Close filters"
          >
            <X size={18} />
          </button>
          {/* Desktop collapse button — hidden on mobile */}
          <button aria-label="Collapse filters"
            onClick={toggleCollapsed}
            title="Collapse filters"
            className="hidden md:block p-1 text-gray-500 hover:text-gray-50 rounded transition-colors"
          >
            <ChevronLeft size={16} />
          </button>
        </div>
      </div>

      {/* Context-aware content — differs by active view */}
      {renderContent()}
    </div>
  );
};

export default SideBar;
